import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import NewsCard from "@/components/base/NewsCard";
import { getPublicNews, getPublicNewsById } from "@/lib/webContentApi";
import { NewsItem } from "@/lib/webContentTypes";

const categoryColors: Record<string, string> = {
  Noticias: "bg-sky-100 text-sky-700",
  Actividades: "bg-emerald-100 text-emerald-700",
  Comunicados: "bg-amber-100 text-amber-700",
  "Cortes programados": "bg-red-100 text-red-700",
  "Avisos importantes": "bg-orange-100 text-orange-700",
};

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [news, setNews] = useState<NewsItem | null>(null);
  const [related, setRelated] = useState<NewsItem[]>([]);

  useEffect(() => {
    if (!id) {
      setNews(null);
      setRelated([]);
      return;
    }

    getPublicNewsById(id)
      .then((value) => setNews(value))
      .catch(() => setNews(null));

    getPublicNews(4)
      .then((items) => setRelated(items.filter((n) => n.id !== id && n.active).slice(0, 3)))
      .catch(() => setRelated([]));
  }, [id]);

  if (!news) {
    return (
      <main className="pt-32 pb-20 min-h-screen flex flex-col items-center justify-center text-center px-4">
        <i className="ri-newspaper-line text-6xl text-slate-300 mb-4"></i>
        <h2 className="text-2xl font-bold text-slate-700 mb-2">Noticia no encontrada</h2>
        <p className="text-slate-500 mb-6">La noticia que buscas no existe o fue eliminada.</p>
        <Link to="/noticias" className="px-6 py-3 rounded-full bg-sky-500 text-white font-semibold cursor-pointer whitespace-nowrap">
          Volver a noticias
        </Link>
      </main>
    );
  }

  const badgeColor = categoryColors[news.category] ?? "bg-slate-100 text-slate-700";

  return (
    <main className="pt-24 pb-20 min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8">
          <Link to="/" className="hover:text-sky-600 cursor-pointer">Inicio</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/noticias" className="hover:text-sky-600 cursor-pointer">Noticias</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-slate-600 truncate max-w-xs">{news.title}</span>
        </nav>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badgeColor}`}>
            {news.category}
          </span>
          <span className="text-slate-400 text-sm">
            {new Date(news.date).toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" })}
          </span>
        </div>

        <h1
          className="text-3xl md:text-4xl font-extrabold text-sky-900 mb-6 leading-tight"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {news.title}
        </h1>

        <div className="w-full h-64 md:h-96 rounded-2xl overflow-hidden mb-8">
          <img
            src={news.imageUrl}
            alt={news.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <p className="text-lg text-slate-600 font-medium leading-relaxed mb-6 border-l-4 border-sky-400 pl-5 italic">
          {news.summary}
        </p>

        <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed text-base">
          {news.content.split("\n").map((p, i) => (
            <p key={i} className="mb-4">{p}</p>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-slate-100">
          <Link
            to="/noticias"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-sky-500 text-sky-600 font-semibold hover:bg-sky-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Volver a noticias
          </Link>
        </div>
      </div>

      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-16">
          <h3 className="text-2xl font-bold text-sky-900 mb-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Otras noticias
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((n) => (
              <NewsCard key={n.id} news={n} />
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
